import React from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

/**
 * "Commandes par jour" — line chart (with a subtle area gradient fill).
 *
 * Props:
 *  - data:        [{key, date, label, count}] — comes zero-filled from the API.
 *  - granularity: 'day' | 'week' | 'month' (default 'day') — controls the
 *                 dropdown label rendered on top-right.
 *  - onGranularityChange: (newGranularity) => void
 *  - loading, error: usual states.
 */
const GRAN_LABELS = {
    day: 'Par jour',
    week: 'Par semaine',
    month: 'Par mois',
};

function ChartTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg px-3 py-2 text-xs">
            <div className="font-semibold text-[#1A1D2E]">{p.label}</div>
            <div className="text-[#6B7280] mt-0.5">
                <span className="font-medium text-[#4B3FD8]">{Number(p.count).toLocaleString('fr-FR')}</span>{' '}
                commande{p.count > 1 ? 's' : ''}
            </div>
        </div>
    );
}

export default function DailyChart({ data, granularity, onGranularityChange, loading, error }) {
    const series = data?.series || [];
    const hasData = series.some((d) => d.count > 0);

    return (
        <div data-testid="daily-chart-card" className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-4 space-y-3 h-full flex flex-col">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="text-base font-semibold text-[#1A1D2E]">Commandes par jour</h3>
                    <p className="text-xs text-[#6B7280]">Évolution du volume sur la période sélectionnée</p>
                </div>
                <select
                    data-testid="daily-chart-granularity"
                    value={granularity}
                    onChange={(e) => onGranularityChange(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                >
                    {Object.entries(GRAN_LABELS).map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                    ))}
                </select>
            </div>

            <div className="h-[300px]">
                {loading && (
                    <div data-testid="daily-chart-loading" className="h-full p-2">
                        <div className="h-full w-full rounded-lg bg-[#F5F6FA] overflow-hidden relative">
                            <span className="absolute inset-0 block animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent" />
                        </div>
                    </div>
                )}
                {!loading && error && (
                    <div data-testid="daily-chart-error" className="h-full flex items-center justify-center text-sm text-[#EF4444]">
                        {typeof error === 'string' ? error : 'Erreur'}
                    </div>
                )}
                {!loading && !error && !hasData && (
                    <div data-testid="daily-chart-empty" className="h-full flex flex-col items-center justify-center gap-1 text-sm text-[#6B7280]">
                        <div className="w-10 h-10 rounded-full bg-[#4B3FD8]/10 flex items-center justify-center mb-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4B3FD8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-4 4 4 6-6"/></svg>
                        </div>
                        <div className="text-[#1A1D2E] font-semibold text-[13px]">Aucune donnée pour cette période</div>
                        <div className="text-xs">Ajustez la période ou les filtres pour visualiser l'évolution.</div>
                    </div>
                )}
                {!loading && !error && hasData && (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={series} margin={{ top: 8, right: 12, left: -12, bottom: 4 }}>
                            <defs>
                                <linearGradient id="daily-fill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#4B3FD8" stopOpacity={0.28} />
                                    <stop offset="100%" stopColor="#4B3FD8" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fill: '#6B7280', fontSize: 11 }}
                                axisLine={{ stroke: '#E5E7EB' }}
                                tickLine={false}
                                interval="preserveStartEnd"
                                minTickGap={20}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fill: '#6B7280', fontSize: 11 }}
                                axisLine={{ stroke: '#E5E7EB' }}
                                tickLine={false}
                                width={40}
                            />
                            <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#4B3FD8', strokeOpacity: 0.15, strokeWidth: 2 }} />
                            <Area
                                type="monotone"
                                dataKey="count"
                                stroke="#4B3FD8"
                                strokeWidth={2}
                                fill="url(#daily-fill)"
                                dot={{ r: 3, fill: '#4B3FD8', strokeWidth: 0 }}
                                activeDot={{ r: 5, fill: '#4B3FD8', stroke: '#FFFFFF', strokeWidth: 2 }}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
